import { IObservable, SeenTracker, Subject } from '~/rage/utils';
import {
	BarberShopItemConfig,
	BarberShopSection,
	BarberShopSectionConfig,
	Gender,
	IClientConfigs,
	IPedAppearanceValue,
	PedOverlaySection,
	WalletType,
} from '~/shared';
import { IBarberShopService } from './barberShopServiceTypes';

export class BarberShopServiceStub implements IBarberShopService {
	private items = new Subject<BarberShopItemConfig[]>([
		{
			index: -1,
			shopSection: BarberShopSection.FACIAL_HAIR,
			overlaySection: PedOverlaySection.FacialHair,
			price: 0,
		},
		{
			index: 0,
			shopSection: BarberShopSection.FACIAL_HAIR,
			overlaySection: PedOverlaySection.FacialHair,
			price: 1000,
		},
		{
			index: 1,
			shopSection: BarberShopSection.FACIAL_HAIR,
			overlaySection: PedOverlaySection.FacialHair,
			price: 2000,
		},
		{
			index: -1,
			shopSection: BarberShopSection.EYEBROWS,
			overlaySection: PedOverlaySection.Eyebrows,
			price: 0,
		},
		{
			index: 0,
			shopSection: BarberShopSection.EYEBROWS,
			overlaySection: PedOverlaySection.Eyebrows,
			price: 10000,
		},
		{
			index: 2,
			shopSection: BarberShopSection.EYEBROWS,
			overlaySection: PedOverlaySection.Eyebrows,
			price: 2000,
		},
		...Array.from({ length: 100 }, (_, index) => ({
			index: 1,
			shopSection: BarberShopSection.FACIAL_HAIR,
			overlaySection: PedOverlaySection.FacialHair,
			price: 1000 + index,
		})),
	] as BarberShopItemConfig[]);

	private sections = new Subject<BarberShopSectionConfig[]>([
		{
			id: BarberShopSection.FACIAL_HAIR,
			name: 'Борода',
			withColor: true,
			withOpacity: true,
		},
		{
			id: BarberShopSection.EYEBROWS,
			name: 'Брови',
			withColor: true,
			withOpacity: true,
		},
		{
			id: BarberShopSection.MAKEUP,
			name: 'Макияж',
			withColor: true,
			withOpacity: false,
		},
		{
			id: BarberShopSection.BLUSH,
			name: 'Румянец',
			withColor: true,
			withOpacity: false,
		},
	]);
	private overlaysFemale = new Subject<
		IClientConfigs['appearance']['overlays']
	>([]);
	private overlaysMale = new Subject<IClientConfigs['appearance']['overlays']>(
		[],
	);
	private hairColors = new Subject<IClientConfigs['appearance']['hairColors']>([
		{
			index: 0,
			hex: '#1C1C1C',
			isEnabled: true,
			isCharCreationEnabled: true,
			isBarberShopEnabled: true,
		},
		{
			index: 1,
			hex: '#4B2E2E',
			isEnabled: true,
			isCharCreationEnabled: true,
			isBarberShopEnabled: true,
		},
		{
			index: 2,
			hex: '#C0A080',
			isEnabled: true,
			isCharCreationEnabled: true,
			isBarberShopEnabled: true,
		},
	]);
	private makeupColors = new Subject<
		IClientConfigs['appearance']['makeupColors']
	>([
		{
			index: 0,
			hex: '#B56D5E',
			isEnabled: true,
			isCharCreationEnabled: true,
			isBarberShopEnabled: true,
		},
		{
			index: 1,
			hex: '#C6002B',
			isEnabled: true,
			isCharCreationEnabled: true,
			isBarberShopEnabled: true,
		},
		{
			index: 2,
			hex: '#E37DA2',
			isEnabled: true,
			isCharCreationEnabled: true,
			isBarberShopEnabled: true,
		},
	]);

	private seenTracker = new SeenTracker(null);

	private readonly RULES = {
		isEnabled: true,
		isCharCreationEnabled: true,
		isBarberShopEnabled: false,
	} as const;

	constructor() {
		const overlaysFemale = [
			{
				section: PedOverlaySection.Blemishes,
				index: -1,
				name: 'Отсутствует',
				gender: Gender.FEMALE,
			},
			{
				section: PedOverlaySection.Blemishes,
				index: 0,
				name: 'Краснуха',
				gender: Gender.FEMALE,
			},
			{
				section: PedOverlaySection.Blemishes,
				index: 1,
				name: 'Корь',
				gender: Gender.FEMALE,
			},
			{
				section: PedOverlaySection.Eyebrows,
				index: 0,
				name: 'Аккуратные',
				gender: Gender.FEMALE,
			},
			{
				section: PedOverlaySection.Eyebrows,
				index: 1,
				name: 'Модные',
				gender: Gender.FEMALE,
			},
			{
				section: PedOverlaySection.Eyebrows,
				index: 2,
				name: 'Клеопатра',
				gender: Gender.FEMALE,
			},
		];
		this.overlaysFemale.next(
			overlaysFemale.map((e) => ({ ...e, ...this.RULES })),
		);

		const overlaysMale = [
			{
				section: PedOverlaySection.Blemishes,
				index: -1,
				name: 'Отсутствует',
				gender: Gender.MALE,
			},
			{
				section: PedOverlaySection.Blemishes,
				index: 0,
				name: 'Краснуха',
				gender: Gender.MALE,
			},
			{
				section: PedOverlaySection.Blemishes,
				index: 1,
				name: 'Корь',
				gender: Gender.MALE,
			},
			{
				section: PedOverlaySection.FacialHair,
				index: -1,
				name: 'Отсутствует',
				gender: Gender.MALE,
			},
			{
				section: PedOverlaySection.FacialHair,
				index: 0,
				name: 'Легкая щетина',
				gender: Gender.MALE,
			},
			{
				section: PedOverlaySection.FacialHair,
				index: 1,
				name: 'Бальбо',
				gender: Gender.MALE,
			},
			{
				section: PedOverlaySection.Eyebrows,
				index: 0,
				name: 'Аккуратные',
				gender: Gender.MALE,
			},
			{
				section: PedOverlaySection.Eyebrows,
				index: 1,
				name: 'Модные',
				gender: Gender.MALE,
			},
			{
				section: PedOverlaySection.Eyebrows,
				index: 2,
				name: 'Клеопатра',
				gender: Gender.MALE,
			},
		];
		this.overlaysMale.next(overlaysMale.map((e) => ({ ...e, ...this.RULES })));
	}

	public getOverlayConfigsByGender(
		gender: Gender,
	): IObservable<IClientConfigs['appearance']['overlays']> {
		return gender === Gender.FEMALE ? this.overlaysFemale : this.overlaysMale;
	}

	public getSections(): IObservable<BarberShopSectionConfig[]> {
		return this.sections;
	}

	private currentAppearance = new Subject<IPedAppearanceValue | null>({
		gender: Gender.MALE,
		parents: { momIndex: 21, dadIndex: 0, faceBlend: 0, skinBlend: 0 },
		faceMorphs: [],
		overlays: [
			{
				section: PedOverlaySection.Eyebrows,
				index: 2,
				colorPrimary: 0,
				colorSecondary: 0,
				colorOpacity: 0,
			},
			{
				section: PedOverlaySection.FacialHair,
				index: 0,
				colorPrimary: 1,
				colorSecondary: 2,
				colorOpacity: 0.5,
			},
		],
	});

	public getItems(): IObservable<BarberShopItemConfig[]> {
		return this.items;
	}

	public getCurrentAppearance(): IObservable<IPedAppearanceValue | null> {
		return this.currentAppearance;
	}

	public getHairColors(): IObservable<
		IClientConfigs['appearance']['hairColors']
	> {
		return this.hairColors;
	}

	public getMakeupColors(): IObservable<
		IClientConfigs['appearance']['makeupColors']
	> {
		return this.makeupColors;
	}

	public async buyItem(
		overlaySection: PedOverlaySection,
		index: number,
		primaryColorIndex: number,
		secondaryColorIndex: number,
		opacity: number,
		walletType: WalletType,
	): Promise<void> {
		console.log(
			'barberShop: buyItem',
			`${overlaySection}:${index}`,
			primaryColorIndex,
			secondaryColorIndex,
			opacity,
			walletType,
		);
	}

	public async previewItem(
		overlaySection?: PedOverlaySection | null,
		index?: number,
		primaryColorIndex?: number,
		secondaryColorIndex?: number,
		opacity?: number,
	): Promise<void> {
		if (overlaySection === null || index === undefined) {
			console.log('barberShop: resetPreview');
			return Promise.resolve();
		}

		console.log(
			'barberShop: previewItem',
			`${overlaySection}:${index}`,
			primaryColorIndex,
			secondaryColorIndex,
			opacity,
		);
	}

	public isUnseen(path: string): boolean {
		return this.seenTracker.isUnseen(path);
	}

	public markSeen(path: string): void {
		console.log('barberShop:markSeen', path);
		this.seenTracker.markAsSeen(path);
	}

	public async exitMenu(): Promise<void> {
		console.log('barberShop: exitMenu');
	}
}
